import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { Button, Grid, Group, Layout, Page } from "@/comn/components";
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage } from "@/comn/hooks";
import {
    APIS,
    SF_LCNE_USR_SRCH,
    SG_LCNE_USR_EXCEL_LIST,
    SG_LCNE_USR_LIST,
    URLS,
} from "@/tra/tancis/ptli/usr/lcneusr/services/PtliLcneUsrService";
import dayjs from "dayjs";

/*
 * License User List
 * !== 면허 사용자 목록 ==!
 */
export const PTLI0906001Q = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_lcmi0001", "auth_ptli0025", "auth_eswi0001", "auth_ptlianyADM"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal();
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const [load, setLoad] = useState(false);
    const [selectedRows, setSelectedRows] = useState<any|null>(null);   
    const [showFfaBtn, setShowFfaBtn] = useState(false); 
    const [ffaSwchStts, setFfaSwchStts] = useState(false);

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "LCM0001" },
            { area: "comnCd", comnCd: "PTL0001" },
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // License User List Search !== 면허 사용자 목록 검색 ==!
        lcneUsrListSrch: useForm({
            defaultSchema: SF_LCNE_USR_SRCH,
            defaultValues: {
                ...(pgeStore?.form || {}),
            },
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        // License User List !== 면허 사용자 목록 ==!
        lcneUsrList: useGrid({
            defaultSchema: SG_LCNE_USR_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
        lcneUsrExcelList: useGrid({
            defaultSchema: SG_LCNE_USR_EXCEL_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        //Check License User List !==  면허 사용자 목록 조회==!
        getLcneUsrList: useFetch({
            api: (page = grid.lcneUsrList.page) =>
                APIS.getLcneUsrList(form.lcneUsrListSrch.getValues(), page, grid.lcneUsrList.size),

            enabled: comnUtils.isEmpty(form.lcneUsrListSrch.errors),
            
            key: [grid.lcneUsrList.page, grid.lcneUsrList.size],
            
            onSuccess: (data:any) => {
                // ffaSwchStts
                setFfaSwchStts("Y" === data?.ffaSwchStts?.content);
                setStore(pgeUid, {
                    form: form.lcneUsrListSrch.getValues(),
                    page: grid.lcneUsrList.page,
                    size: grid.lcneUsrList.size,
                });
            
            const value = form.lcneUsrListSrch.getValue("lcneUsrOrg");
            const result = ["COM00851", "COM00852", "COM00853"].includes(value); // COM00851: TAFFA, COM00852: TACAS, COM00853: CALAT
                        
            // enable and disable checkbox
            grid.lcneUsrList.setOption("checkbox", result);
            setShowFfaBtn(result);
            },

            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
            showToast: true,
        }),

        //Check License User List !==  면허 사용자 목록 엑셀셀 조회==!
        getLcneUsrExcelList: useFetch({
            api: (data: any) => (load ? APIS.getLcneUsrExcelList(data) : null),
            enabled: comnUtils.isEmpty(form.lcneUsrListSrch.errors),
            onSuccess: () => {
                if (load) {
                    grid.lcneUsrExcelList.exportExcel({
                        // Internal Portal Customs User Information !== 내부 포털_세관_사용자 정보 ==!
                        excelName: `${t("L_INT_PTL")}_${t("L_CO_NM")}_${t("T_USR_INFO")}_${dayjs().format(
                            "DD/MM/YYYY HHmm",
                        )}`,
                        sheetName: "Sheet1",
                    });
                }
                setLoad(false);
            },
            onError: () => {},
            showToast: true,
        }),

        // exempt members in batch
        updateLcneUsrFfaexpmtBatch: useFetch({
            api: (data) => APIS.updateLcneUsrFfaexpmtBatch(data),
            onSuccess: (data) => {
                modal.openModal({
                    content: "msg.com.00062", // Successfully saved., !== 정상적으로 저장되었습니다. ==!
                });
                handler.onSrchBtnClick();
                setSelectedRows(null); // Clear selected rows after successful exemption
            },
            onError: (error) => {
                const data = error.response.data;
                modal.openModal({
                    content: data.errorMessage,
                });
            },
            showToast: true,
        }),
    };


    const render = {
        checkbox: (row: any) => {
            return row.exmptStts === "EXEMPTED"; // row disable for Exempted row
        },
        cell: {
            coTin: (props: any) => {
                const { rowValues, value } = props;
                return <Link to={`/ptli/usr/lcneusr/ptli0906004q/${rowValues.coTin}`}>{value}</Link>;
            },
        },
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Click Search Button !== 검색버튼 ==!
        onSrchBtnClick: (page: number = 0) => {
            grid.lcneUsrList.setPage(page);
            fetch.getLcneUsrList.fetch(page);
        },
        //Click Registration Button  !== 등록 버튼 클릭 ==!
        onNewBtnClick: () => navigate(URLS.ptli0906002s),

        onExemptBtnClick: () => {
            const selectedRows = grid.lcneUsrList.getChecked();
        }, 
        handleSelectedRows: () => {
            const selctdRows = grid.lcneUsrList.getChecked();
            setSelectedRows(selctdRows);
        },
        handleMultipleExempt: () => { 
            
            
            if (selectedRows?.length > 0) {
                // /update-ffa/batch
                modal.openModal({
                    content: "Are you sure you want to exempt the selected users from FFA Enforcement?",
                    onConfirm:  async () => {
                        // if (!comnUtils.isEmpty(selectedRows?.length < 0)) {
                        //     toast.showToast({ type: "warning", content: "msg.00002" });
                        //     return;
                        // }
                        fetch.updateLcneUsrFfaexpmtBatch.fetch(selectedRows);
                    },
                });

            } else {
                modal.openModal({
                    content: t("No rows selected for exemption. Please select at least one user to exempt."),
                }); // Please select at least one user to exempt. !== 면제할 사용자를 최소 1명 이상 선택해주세요. ==!
                return;
            }
        }
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        handler.onSrchBtnClick(pgeStore?.page);
    }, []);

    useEffect(() => {
        const store = getStore(pgeUid);
        if (!comnUtils.isEmpty(store) || load) {
            fetch.getLcneUsrExcelList.fetch(store.form);
        }
    }, [load]);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.lcneUsrListSrch.schema.lcneTpCd} />
                            <Group.Control {...form.lcneUsrListSrch.schema.coTin} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.lcneUsrListSrch.schema.coNm} />
                            <Group.Control {...form.lcneUsrListSrch.schema.usrStatCd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.lcneUsrListSrch.schema.lcneUsrOrg} />
                        </Group.Row>
                    </Group.Section>
                    <Layout>
                        <Layout.Left>
                            <Button role={"reset"} onClick={() => form.lcneUsrListSrch.reset()} />
                        </Layout.Left>
                        <Layout.Right>
                            <Button
                                role={"search"}
                                onClick={() => {
                                    handler.onSrchBtnClick();
                                }}
                            />
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Layout direction="row">
                        <Layout.Left>
                            <Button
                                role="excelDown"
                                onClick={() => {
                                    setLoad(true);
                                }}
                            ></Button>
                        </Layout.Left>
                    </Layout>
                    <Group.Section>
                        <Grid
                            {...grid.lcneUsrList.grid}
                            data={fetch.getLcneUsrList.data?.lcneUsrList}
                            render={render}
                            onRowCheck={handler.handleSelectedRows}
                        />
                    </Group.Section>
                </Group.Body>
            </Group>
            <Layout>
                <Layout.Left>
                    <Layout.Button role={"new"} onClick={handler.onNewBtnClick} />
                </Layout.Left>
                <Layout.Right>
                    {showFfaBtn && ffaSwchStts && <Layout.Button onClick={handler.handleMultipleExempt}>
                    Exempt {selectedRows?.length ? `(${selectedRows.length})` : 0} Selected User
                </Layout.Button>}
                </Layout.Right>
            </Layout>
            <Group hidden>
                <Grid {...grid.lcneUsrExcelList.grid} data={fetch.getLcneUsrExcelList.data?.lcneUsrExcelList} />
            </Group>
        </Page>
    );
};
